import { Client } from '@stomp/stompjs';
import { useCallback, useEffect, useRef } from 'react';
import SockJS from 'sockjs-client';

const useStomp = (
  sockJSUrl,
  subscribeUrl,
  onMessageReceived,
  onError,
  enabled = true
) => {
  const clientRef = useRef(null);
  const lastMessageRef = useRef(null); // To store the last received message
  const isInitializedRef = useRef(false);

  // Use useCallback to prevent recreation of functions on every render
  const handleMessageReceived = useCallback(
    (receivedMsg) => {
      // Check if the received message is different from the last one
      if (
        JSON.stringify(receivedMsg) !== JSON.stringify(lastMessageRef.current)
      ) {
        onMessageReceived(receivedMsg);
        lastMessageRef.current = receivedMsg; // Update the last received message
      }
    },
    [onMessageReceived]
  );

  const handleError = useCallback(
    (frame) => {
      onError(frame);
    },
    [onError]
  );

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitializedRef.current) {
      return;
    }

    const initializeStomp = () => {
      if (clientRef.current && clientRef.current.connected) {
        return; // If the client is already connected, do not create a new instance
      }

      const client = new Client({
        webSocketFactory: () => new SockJS(sockJSUrl),
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
      });

      client.onConnect = (frame) => {
        client.subscribe(subscribeUrl, (message) => {
          const receivedMsg = JSON.parse(message.body);
          handleMessageReceived(receivedMsg);
        });
      };

      client.onStompError = (frame) => {
        handleError(frame);
      };

      client.onWebSocketError = (error) => {
        console.error('WebSocket error:', error);
      };

      client.onWebSocketClose = (event) => {
        isInitializedRef.current = false; // Allow reconnection
      };
      client.activate();
      clientRef.current = client;
      isInitializedRef.current = true;
    };

    if (enabled) {
      initializeStomp();
    }

    // Cleanup when the component unmounts
    return () => {
      if (clientRef.current && clientRef.current.connected) {
        clientRef.current.deactivate();
      }
      clientRef.current = null;
      lastMessageRef.current = null;
      isInitializedRef.current = false;
    };
  }, [sockJSUrl, subscribeUrl, enabled, handleError, handleMessageReceived]);

  const sendMessage = (destination, headers, body) => {
    if (!clientRef.current || !clientRef.current.connected) {
      return;
    }

    clientRef.current.publish({
      destination,
      headers,
      body: JSON.stringify(body),
    });
  };

  const isStompConnected = clientRef.current?.connected || false;

  return { sendMessage, isStompConnected };
};

export default useStomp;
